/**
 * Schedule step component
 * Step 4 of schedule wizard - name, interval preset, cron expression
 */

import { useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Input, Typography, Radio, Space, Card, Alert } from 'antd';
import { ClockCircleOutlined, EditOutlined } from '@ant-design/icons';
import { CronExpressionParser } from 'cron-parser';
import type { IntervalPreset } from '@/types/schedule';
import type { ScheduleFormData } from './ScheduleWizard';

const { Title, Text, Paragraph } = Typography;

const PRESET_OPTIONS: { value: IntervalPreset; label: string; description: string }[] = [
  { value: 'daily', label: 'Daily', description: '3:00 AM UTC every day' },
  { value: 'weekly', label: 'Weekly', description: 'Monday at 3:00 AM UTC' },
  { value: 'biweekly', label: 'Biweekly', description: '1st & 15th at 3:00 AM UTC' },
  { value: 'monthly', label: 'Monthly', description: '1st of month at 3:00 AM UTC' },
  { value: 'custom', label: 'Custom', description: 'Enter a cron expression (UTC)' },
];

const PRESET_CRON: Record<Exclude<IntervalPreset, 'custom'>, string> = {
  daily: '0 3 * * *',
  weekly: '0 3 * * 1',
  biweekly: '0 3 1,15 * *',
  monthly: '0 3 1 * *',
};

/**
 * Compute next N run times from a cron expression
 */
function getNextRuns(cronExpr: string, count: number = 3): Date[] {
  try {
    const interval = CronExpressionParser.parse(cronExpr);
    const dates: Date[] = [];
    for (let i = 0; i < count; i++) {
      dates.push(interval.next().toDate());
    }
    return dates;
  } catch {
    return [];
  }
}

function formatDate(date: Date): string {
  return date.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ScheduleStep() {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<ScheduleFormData>();

  const intervalPreset = watch('intervalPreset');
  const cronExpression = watch('cronExpression');
  const searchQuery = watch('searchQuery');
  const locationName = watch('locationName');

  // Resolve the effective cron expression
  const effectiveCron = useMemo(() => {
    if (intervalPreset === 'custom') {
      return cronExpression || '';
    }
    return PRESET_CRON[intervalPreset] || '';
  }, [intervalPreset, cronExpression]);

  // Compute next run times
  const nextRuns = useMemo(() => {
    if (!effectiveCron) return [];
    return getNextRuns(effectiveCron, 3);
  }, [effectiveCron]);

  // Validate custom cron
  const cronError = useMemo(() => {
    if (intervalPreset !== 'custom' || !cronExpression) return null;
    try {
      CronExpressionParser.parse(cronExpression);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Invalid cron expression';
    }
  }, [intervalPreset, cronExpression]);

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <Title level={4} style={{ marginBottom: 8 }}>
        Configure schedule
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Give your schedule a name and choose how often it should run.
      </Paragraph>

      {/* Schedule name */}
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Schedule Name *
        </Text>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              prefix={<EditOutlined />}
              placeholder="e.g., Monthly Restaurants Germany"
              status={errors.name ? 'error' : undefined}
            />
          )}
        />
        {errors.name && (
          <Text type="danger" style={{ fontSize: 13 }}>
            {errors.name.message as string}
          </Text>
        )}
      </div>

      {/* Interval preset */}
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Run Frequency
        </Text>
        <Controller
          name="intervalPreset"
          control={control}
          render={({ field }) => (
            <Radio.Group
              {...field}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {PRESET_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value} style={{ width: '100%' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Text strong>{option.label}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {option.description}
                      </Text>
                    </div>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          )}
        />
      </div>

      {/* Custom cron expression */}
      {intervalPreset === 'custom' && (
        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Cron Expression *
          </Text>
          <Controller
            name="cronExpression"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="0 3 * * 1"
                style={{ fontFamily: 'monospace' }}
                status={cronError ? 'error' : undefined}
              />
            )}
          />
          {cronError && (
            <Text type="danger" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
              {cronError}
            </Text>
          )}
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            Format: minute hour day-of-month month day-of-week
          </Text>
        </div>
      )}

      {/* Next run times preview */}
      {nextRuns.length > 0 && (
        <Card size="small" style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <ClockCircleOutlined />
              <Text strong>Upcoming runs (preview):</Text>
            </Space>
            {nextRuns.map((date, i) => (
              <Text key={i} type="secondary" style={{ paddingLeft: 22 }}>
                {formatDate(date)}
              </Text>
            ))}
          </Space>
        </Card>
      )}

      {/* Summary */}
      <Alert
        type="info"
        showIcon
        message="Schedule Summary"
        description={
          <div>
            <div>Search: <Text strong>{searchQuery || '—'}</Text></div>
            <div>Location: <Text strong>{locationName || 'Custom area'}</Text></div>
            <div>Frequency: <Text strong>
              {PRESET_OPTIONS.find((o) => o.value === intervalPreset)?.label || intervalPreset}
            </Text></div>
          </div>
        }
      />
    </div>
  );
}

export default ScheduleStep;
